import { useAtomValue, useSetAtom } from 'jotai'
import { Plus } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { AccountCardButton } from '@/src/components/accounts/account-card'
import { Button } from '@/src/components/ui/button'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { walletConnectionQueueAtom } from '@/src/stores/accounts/detection'
import {
  pendingAccountsAtom,
  walletAccountsAtom,
} from '@/src/stores/accounts/wallets'
import { usePortfolioView } from '@/src/stores/portfolio-drawer/actions'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { PendingAccountRow } from '../../accounts/pending-account-row'
import { WatchWalletAddressDialog } from '../../accounts/watch-address'

export function Wallets() {
  const { updateView, goBack } = usePortfolioView()
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)

  const { ampli } = useAmplitudeAnalytics()
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  useEffect(() => {
    if (groupedAccounts.length === 0) {
      goBack()
    }
  }, [groupedAccounts, goBack])

  const [isWatchDialogOpen, setIsWatchDialogOpen] = useState(false)
  const browserExtensionAccounts = groupedAccounts.filter(
    (account) => account.type === 'BrowserExtension',
  )
  const watchingAccounts = groupedAccounts.filter(
    (account) => account.type === 'Mobile' || account.type === 'WatchAddress',
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col'
    >
      <div className='flex flex-col gap-2'>
        <PendingWalletList />
        <div className='flex items-center justify-between py-2'>
          <h6 className='text-xl leading-8 font-semibold text-primary'>
            Your wallets
          </h6>
          <Button
            type='button'
            size='sm'
            variant='secondary'
            className='size-8'
            onClick={() => {
              ampli?.walletConnectWalletClicked({
                position: 'portfolio',
              })
              setDialogState(openSelectWalletsDialog())
            }}
          >
            <Plus size={24} className='text-primary min-w-6' />
          </Button>
        </div>
        {browserExtensionAccounts.length > 0 ? (
          <div className='grid grid-cols-2 gap-2'>
            {browserExtensionAccounts.map((account) => (
              <AccountCardButton
                key={account.grouping_key}
                className='shadow-lg'
                account={account}
                onClick={() => {
                  updateView({
                    view: 'manage-wallet',
                    metadata: {
                      accountGroupingKey: account.grouping_key,
                      walletName: account.provider_info.name,
                    },
                  })
                }}
              />
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center gap-4 rounded-3xl p-4 border border-button-outline-border'>
            <Image src='/assets/backpack.png' alt='' width={72} height={72} />
            <div className='flex flex-col items-center text-center'>
              <span className='text-primary font-semibold'>Add a wallet</span>
              <span className='text-xs text-secondary font-normal'>
                Connect your existing wallets.
              </span>
            </div>
          </div>
        )}
      </div>
      <div className='flex flex-col gap-2 pt-6'>
        <div className='flex items-center justify-between py-2'>
          <h6 className='text-xl leading-8 font-semibold text-primary'>
            Watching
          </h6>
          <Button
            type='button'
            size='sm'
            variant='secondary'
            className='size-8'
            onClick={() => setIsWatchDialogOpen(true)}
          >
            <Plus size={24} className='text-primary min-w-6' />
          </Button>
        </div>
        {watchingAccounts.length > 0 ? (
          <div className='grid grid-cols-2 gap-2'>
            {watchingAccounts.map((account) => (
              <AccountCardButton
                key={account.grouping_key}
                className='shadow-lg'
                account={account}
                onClick={() => {
                  updateView({
                    view: 'manage-wallet',
                    metadata: {
                      accountGroupingKey: account.grouping_key,
                      walletName: account.provider_info.name,
                    },
                  })
                }}
              />
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center gap-4 rounded-3xl p-4 border border-button-outline-border'>
            <Image src='/assets/binocular.png' alt='' width={87} height={53} />
            <div className='flex flex-col items-center text-center'>
              <span className='text-primary font-semibold'>
                Watch your wallet
              </span>
              <span className='text-xs text-secondary font-normal'>
                Add a view-only wallet. Ideal for cold wallets or shared
                wallets.
              </span>
            </div>
          </div>
        )}
        <WatchWalletAddressDialog
          open={isWatchDialogOpen}
          onOpenChange={setIsWatchDialogOpen}
        />
      </div>
    </motion.div>
  )
}

function PendingWalletList() {
  const { groupedAccounts: pendingGroupedAccounts } =
    useAtomValue(pendingAccountsAtom)
  const walletConnectionQueue = useAtomValue(walletConnectionQueueAtom)

  if (pendingGroupedAccounts.length === 0) {
    return null
  }

  return pendingGroupedAccounts
    .filter((account) => !walletConnectionQueue.has(account.provider_info.name))
    .map((account) => (
      <PendingAccountRow key={account.grouping_key} account={account} />
    ))
}
